import { Link } from 'react-router-dom'

import { StatusPill } from '@/components/ui/StatusPill'
import {
  formatCurrency,
  formatMonthsAsYears,
  formatRelativeTime,
  humanizeEnum,
} from '@/utils/format'

interface JobCardProps {
  title: string
  organizationName?: string
  organizationLogoUrl?: string | null
  locationCity?: string | null
  locationState?: string | null
  workMode: string
  employmentType: string
  experienceMinMonths?: number | null
  experienceMaxMonths?: number | null
  salaryMin?: number | null
  salaryMax?: number | null
  currency?: string | null
  publishedAt?: string | null
  status?: string
  detailHref: string
  actionLabel?: string
}

function formatExperienceRange(min?: number | null, max?: number | null): string | null {
  if (min == null && max == null) return null
  if (min != null && max != null) return `${formatMonthsAsYears(min)} – ${formatMonthsAsYears(max)}`
  if (min != null) return `${formatMonthsAsYears(min)}+`
  return `up to ${formatMonthsAsYears(max)}`
}

function formatSalaryRange(
  min?: number | null,
  max?: number | null,
  currency?: string | null,
): string | null {
  if (min == null && max == null) return null
  if (min != null && max != null)
    return `${formatCurrency(min, currency ?? 'INR')} – ${formatCurrency(max, currency ?? 'INR')}`
  if (min != null) return `${formatCurrency(min, currency ?? 'INR')}+`
  return `up to ${formatCurrency(max, currency ?? 'INR')}`
}

export function JobCard({
  title,
  organizationName,
  organizationLogoUrl,
  locationCity,
  locationState,
  workMode,
  employmentType,
  experienceMinMonths,
  experienceMaxMonths,
  salaryMin,
  salaryMax,
  currency,
  publishedAt,
  status,
  detailHref,
  actionLabel = 'View Job',
}: JobCardProps) {
  const location = [locationCity, locationState].filter(Boolean).join(', ')
  const experience = formatExperienceRange(experienceMinMonths, experienceMaxMonths)
  const salary = formatSalaryRange(salaryMin, salaryMax, currency)

  return (
    <div className="card custom-card h-100">
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2">
            {organizationLogoUrl ? (
              <img
                src={organizationLogoUrl}
                alt={organizationName ?? title}
                width={40}
                height={40}
                className="rounded"
              />
            ) : (
              <span className="avatar avatar-md avatar-rounded bg-primary-transparent">
                {(organizationName ?? title).charAt(0).toUpperCase()}
              </span>
            )}
            <div>
              <p className="fw-semibold mb-0">{title}</p>
              {organizationName && <p className="fs-12 text-muted mb-0">{organizationName}</p>}
            </div>
          </div>
          {status && <StatusPill status={status} />}
        </div>

        <div className="d-flex flex-wrap gap-2 mb-2">
          {location && <span className="badge bg-light text-dark border">{location}</span>}
          <span className="badge bg-light text-dark border">{humanizeEnum(workMode)}</span>
          <span className="badge bg-light text-dark border">{humanizeEnum(employmentType)}</span>
        </div>

        <div className="fs-13 text-muted mb-3">
          {experience && <div>Experience: {experience}</div>}
          {salary && <div>Salary: {salary}</div>}
        </div>

        <div className="mt-auto d-flex align-items-center justify-content-between">
          {publishedAt ? (
            <span className="fs-12 text-muted">Posted {formatRelativeTime(publishedAt)}</span>
          ) : (
            <span />
          )}
          <Link to={detailHref} className="btn btn-primary btn-sm waves-effect waves-light">
            {actionLabel}
          </Link>
        </div>
      </div>
    </div>
  )
}
